import { Card, CardBody, CardHeader, Divider, Heading, Image, Stack, Text } from '@chakra-ui/react';
import { buildFlightPathString, Show } from '@pelicargo/ui';
import { prettyDate, prettyDockHour } from '@pelicargo/utils';
import { useMemo } from 'react';

import { CopyButton } from '../../components/CopyButton';
import { CopyLineItemsButton } from '../../components/CopyLineItemsButton';
import { InstructionsCard } from '../../components/InstructionCard';
import { PaperworkCard } from '../../components/PaperworkCard';
import { PickupSection } from '../../components/PickupSection';
import { QuoteVariantUI } from '../../components/QuoteVariantTabs/QuoteVariant';
import { RequestDetailsCard } from '../../components/RequestDetailsCard';
import { Timeline } from '../../components/Timeline';
import { Validity } from '../../components/Validity';
import { TrpcOutputs } from '../../config/trpc';
import { useQuoteFlightPath } from '../../hooks/quotes/useQuoteFlightPath';
import { useFlightData } from '../../hooks/useFlightData';

type Props = {
  quote: TrpcOutputs['getFinalQuote']['quote'];
  shipmentForms?: TrpcOutputs['getShipmentFormsByRequestId'];
};

export const ExwBookingConfirmation = ({ quote, shipmentForms }: Props) => {
  const { flightSegments, pickupOrigin, flightPathLabel } = useQuoteFlightPath({ quote });
  const { flightData } = useFlightData(flightSegments, { pickupOrigin });

  const bookedQuoteVariant = quote?.request?.booked_quote_variant;
  const inlandQuote = bookedQuoteVariant?.inland_service_quote_variant?.quote;

  const stopCountLabel = useMemo(() => {
    const numStops = quote?.flight_path?.length - 1;

    if (numStops === 0) return 'Nonstop';
    if (numStops === 1) return '1 stop';
    return `${numStops} stops`;
  }, [quote?.flight_path?.length]);

  return (
    <Stack direction={{ base: 'column', lg: 'row' }} spacing="6">
      <Stack w="full" spacing="6">
        <Show if={!!quote?.booking_instructions}>
          <InstructionsCard instructions={quote?.booking_instructions} />
        </Show>
        <PaperworkCard quote={quote} shipmentForms={shipmentForms} />
        <Card>
          <CardHeader pl="4" pr="4" pt="3" pb="3">
            <Stack>
              <Stack direction="row" align="center" spacing="6">
                <Image src={quote?.provider?.logo_uri} height="48px" width="48px" alt={quote?.provider?.name} />
                <Heading variant="h6">
                  {flightPathLabel || `${quote?.origin_airport_id} → ${quote?.destination_airport_id}`}
                </Heading>
                <Text textStyle="bodySmallRegular">{stopCountLabel}</Text>
                <CopyButton
                  text={buildFlightPathString(flightSegments, {
                    pickupOrigin,
                    originAirportId: quote?.origin_airport_id,
                  })}
                  marginLeft="auto"
                />
              </Stack>
              <Text color="text.subtle" textStyle="captionSmallRegular">
                {quote?.provider?.name}
              </Text>
            </Stack>
          </CardHeader>
          <Stack>
            <Divider orientation="horizontal" />
          </Stack>
          <CardBody>
            <Timeline data={flightData} />
          </CardBody>
        </Card>
        <Card borderRadius="lg" role="group">
          <CardHeader py="3">
            <Heading variant="h5">Pick Up</Heading>
          </CardHeader>
          <CardBody pt="0">
            <PickupSection
              readyDate={prettyDate(inlandQuote?.pickup_ready_at, { timeZone: 'Etc/UTC' })}
              dockHours={`${prettyDockHour(inlandQuote?.from_dock_hours)} - ${prettyDockHour(inlandQuote?.to_dock_hours)}`}
              address={inlandQuote?.delivery_address}
              pickupContact={inlandQuote?.inland_dropoff_contact || {}}
              truckingRef={inlandQuote?.trucking_ref}
              truckCompany={inlandQuote?.provider?.name}
              estimatedPickup={
                inlandQuote?.estimated_pickup_at
                  ? prettyDate(inlandQuote.estimated_pickup_at, { showTime: true, timeZone: 'Etc/UTC' })
                  : undefined
              }
              flex="1"
            />
          </CardBody>
        </Card>
      </Stack>
      <Stack w="full" spacing="6" alignItems="flex-start">
        <Card h="fit-content">
          <CardBody p="4">
            <Stack spacing={6}>
              <Stack direction="row" align="center" justify="space-between" spacing="4" role="group">
                <Heading variant="h5">Cost Summary</Heading>
                <CopyLineItemsButton quote={quote} quoteVariant={bookedQuoteVariant} />
              </Stack>
              <QuoteVariantUI quote={quote} quoteVariant={bookedQuoteVariant} showCopy={false} />
              <Validity quote={quote} />
            </Stack>
          </CardBody>
        </Card>
        <RequestDetailsCard request={quote?.request} quote={quote} />
      </Stack>
    </Stack>
  );
};
